import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Paperclip, X, Loader2 } from 'lucide-react';
import type { FormFieldDef } from '@hcc/types';
import { asDamageMarkup } from '@hcc/types';
import { FieldView, type FieldInvalid } from '../../onboarding/formfields/FieldView';
import { SignatureField } from '../../onboarding/formfields/SignatureField';
import { DamageMarkupField } from '../../onboarding/formfields/DamageMarkupField';
import type { FieldValue } from '../../onboarding/formfields/formValues';
import { readAsDataUrl, FORM_VIDEO_EXTS } from '../../../lib/upload';
import { compressImage } from '../../../lib/image';
import { uploadHandoverVideo } from '../../../hooks/useKitHandover';

export interface PendingDeskFile {
  field_key: string;
  filename: string;
  base64: string;
}
export interface StoredDeskFile {
  field_key: string;
  name: string;
  key: string;
  content_type?: string;
}

interface DeskFormFieldProps {
  field: FormFieldDef;
  value: FieldValue;
  onChange: (next: FieldValue) => void;
  /** Pending inline captures for this field (signature / markup / images). */
  files: PendingDeskFile[];
  setFiles: (files: PendingDeskFile[]) => void;
  /** Streamed video refs for this field. */
  stored: StoredDeskFile[];
  setStored: (refs: StoredDeskFile[]) => void;
  invalid?: FieldInvalid;
  clearInvalid: () => void;
}

/**
 * One field on the desk handover form. Mirrors FormRenderer's special cases
 * but holds everything in memory until the single submit: there is no task
 * draft at the desk, the whole handover happens in one sitting.
 */
export function DeskFormField(props: DeskFormFieldProps) {
  const { field: f, value, onChange, invalid, clearInvalid } = props;

  if (f.type === 'signature') {
    return (
      <div className="space-y-1">
        <FieldLabel field={f} />
        <div className={invalid?.whole ? INVALID_WRAP : ''}>
          <SignatureField
            hasPrior={false}
            onChange={(dataUrl) => {
              props.setFiles(
                dataUrl
                  ? [{ field_key: f.key, filename: `${f.key}.png`, base64: dataUrl }]
                  : [],
              );
              if (dataUrl) clearInvalid();
            }}
          />
        </div>
      </div>
    );
  }

  if (f.type === 'damage_markup') {
    return (
      <div className="space-y-1">
        <FieldLabel field={f} />
        <div className={invalid?.whole ? INVALID_WRAP : ''}>
          <DamageMarkupField
            imageKey={f.image_key ?? ''}
            value={asDamageMarkup(value)}
            onChange={onChange}
            onPng={(png) => {
              props.setFiles(
                png ? [{ field_key: f.key, filename: `${f.key}.png`, base64: png }] : [],
              );
              if (png) clearInvalid();
            }}
          />
        </div>
      </div>
    );
  }

  if (f.type === 'file_upload') {
    return <DeskFileField {...props} />;
  }

  return (
    <FieldView
      field={f}
      value={value}
      invalid={invalid}
      onChange={(v) => {
        onChange(v);
        clearInvalid();
      }}
    />
  );
}

const INVALID_WRAP = 'rounded-md border border-danger/50 bg-danger-soft/10 p-1.5';

function FieldLabel({ field }: { field: FormFieldDef }) {
  return (
    <>
      <span className="block text-sm font-medium text-fg-muted">
        {field.label}
        {field.required ? <span className="text-danger"> *</span> : null}
      </span>
      {field.help ? <span className="block text-xs text-fg-dim">{field.help}</span> : null}
    </>
  );
}

/**
 * Desk file picker: photos are compressed and held as base64 for the submit
 * body; videos stream straight to storage and travel as refs.
 */
function DeskFileField({
  field: f,
  files,
  setFiles,
  stored,
  setStored,
  invalid,
  clearInvalid,
}: DeskFormFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const accept = (f.accept ?? []).map((e) => '.' + e).join(',');
  const canJpeg = (f.accept ?? []).some((e) => e === 'jpg' || e === 'jpeg');

  async function onPick(list: FileList | null) {
    if (!list || list.length === 0) return;
    const chosen = f.multiple ? Array.from(list) : [list[0]!];
    for (const file of chosen) {
      const ext = file.name.toLowerCase().split('.').pop() ?? '';
      try {
        if (FORM_VIDEO_EXTS.includes(ext)) {
          setProgress(0);
          const ref = await uploadHandoverVideo(file, setProgress);
          setStored(
            f.multiple ? [...stored, { field_key: f.key, ...ref }] : [{ field_key: f.key, ...ref }],
          );
          setProgress(null);
        } else {
          const base64 =
            file.type.startsWith('image/') && canJpeg
              ? await compressImage(file, 2000, 0.85)
              : await readAsDataUrl(file);
          if (base64.length > 9_000_000) {
            toast.error(`"${file.name}" is too large. Try a smaller file or photo.`);
            continue;
          }
          const entry = { field_key: f.key, filename: file.name, base64 };
          setFiles(f.multiple ? [...files, entry] : [entry]);
        }
        clearInvalid();
      } catch (err) {
        setProgress(null);
        toast.error(err instanceof Error ? err.message : `Could not add "${file.name}"`);
      }
    }
    if (inputRef.current) inputRef.current.value = '';
  }

  const listed = [
    ...files.map((x) => ({ id: x.filename, label: x.filename, kind: 'file' as const })),
    ...stored.map((x) => ({ id: x.key, label: x.name, kind: 'stored' as const })),
  ];

  return (
    <fieldset className={`block text-sm ${invalid?.whole ? INVALID_WRAP : ''}`}>
      <FieldLabel field={f} />
      <div className="mt-1.5 space-y-2">
        {listed.map((x) => (
          <div
            key={x.id}
            className="flex items-center gap-2 rounded-token-sm border border-line/10 bg-surface-2 px-2.5 py-1.5 text-sm"
          >
            <Paperclip className="h-4 w-4 text-fg-dim" />
            <span className="min-w-0 flex-1 truncate text-fg">{x.label}</span>
            <button
              type="button"
              onClick={() =>
                x.kind === 'file'
                  ? setFiles(files.filter((y) => y.filename !== x.id))
                  : setStored(stored.filter((y) => y.key !== x.id))
              }
              className="rounded-token-sm p-1 text-fg-dim hover:text-danger"
              aria-label="Remove file"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
        {f.multiple || listed.length === 0 ? (
          <input
            ref={inputRef}
            type="file"
            accept={accept || undefined}
            multiple={f.multiple}
            disabled={progress !== null}
            onChange={(e) => onPick(e.target.files)}
            className="block w-full text-sm text-fg-muted file:mr-3 file:rounded-token-sm file:border-0 file:bg-surface-2 file:px-3 file:py-1.5 file:text-sm file:text-fg-muted disabled:opacity-50"
          />
        ) : null}
        {progress !== null ? (
          <div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-surface-2">
              <div
                className="h-full rounded-full bg-accent transition-all"
                style={{ width: `${Math.round(progress * 100)}%` }}
              />
            </div>
            <p className="mt-1 flex items-center gap-1.5 text-xs text-fg-dim">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Uploading video… {Math.round(progress * 100)}%. Keep this open.
            </p>
          </div>
        ) : null}
      </div>
    </fieldset>
  );
}
